// Booking Types

import type { User } from './user';
import type { Vehicle } from './vehicle';
import type { Driver } from './driver';

// ========================================
// V2 API TYPES - Actual Response Structure
// ========================================

/**
 * Booking V2 API Response
 * Matches the ACTUAL flat structure returned by /api/v2/admin/bookings
 */
export interface BookingV2 {
  // IDs
  id: string;
  bookingNumber: string;
  bookingType: string;

  // Service details
  serviceType: string;
  bookingEntityType: 'vehicle' | 'yacht' | 'driver';

  // Status
  status: string;
  paymentStatus: string;

  // Dates
  startDate: string;
  endDate: string;

  // Amounts
  totalAmount: string;
  dailyRate: string;
  securityDeposit: string;

  // Security Deposit Tracking
  securityDepositAmount: string | null;
  securityDepositStatus: 'pending' | 'held' | 'released' | 'partial_charged' | 'charged' | 'refunded' | null;
  securityDepositHeldAt: string | null;
  securityDepositReleasedAt: string | null;

  // Cost Breakdown Fields (V2 now includes these)
  bookingFee: string;
  rentalCost: number;
  vatAmount: number;
  vatRate: number;
  bookingFeeRate: number;
  serviceFeeRate: number;
  serviceFee: number;
  driverFee: number;
  deliveryFee: number;

  // Locations
  pickupLocation: string;
  dropoffLocation: string;

  // Payment
  paymentMethod: string;

  // User fields (flat)
  user_email: string;
  user_phone: string;
  user_first_name: string;
  user_last_name: string;

  // Vehicle fields (flat)
  vehicle_name: string;

  // Host fields (flat)
  hostId: string | null;
  host_first_name: string | null;
  host_last_name: string | null;
  host_email: string | null;

  // Corporate
  corporate_account_id: string | null;
  is_corporate: boolean;

  // Timestamps
  created_at: string;

  // Notes fields
  notes?: string;
  adminNotes?: string;

  // Pricing breakdown object (V2 now includes this)
  costBreakdown?: {
    basePrice?: number;
    rentalCost: number;
    bookingFee: number;
    bookingFeeRate: number;
    serviceFee: number;
    serviceFeeRate: number;
    vat: number;
    vatRate: number;
    securityDeposit: number;
    totalAmount: number;
    hostPayout: number;
    driverFee?: number;
    deliveryFee?: number;
  };

  // Legacy fields for backward compatibility
  pricingBreakdown?: any;
  pricing?: any;
  payment?: any;
}

/**
 * V2 API List Response
 */
export interface BookingV2ListResponse {
  success: boolean;
  data: {
    bookings: BookingV2[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

/**
 * V2 API Single Response
 */
export interface BookingV2Response {
  success: boolean;
  data: BookingV2;
  message?: string;
}

/**
 * V2 API Single Booking Response (nested structure)
 * NOTE: Backend V2 single booking endpoint returns nested structure, unlike the list endpoint
 */
export interface BookingV2SingleResponse {
  _id: string;
  id: string;
  bookingId: string;
  bookingRef: string;
  bookingReference: string;
  status: string;
  paymentStatus: string;
  customer: {
    id: string;
    name: string;
    email: string;
    phone: string;
  };
  vehicle: {
    id: string;
    name: string;
    make: string;
    model: string;
    year: string;
    licensePlate: string;
    type: string;
    image: string | null;
  };
  booking: {
    startDate: string;
    endDate: string;
    startTime: string;
    endTime: string;
    type: string;
    entityType: string;
  };
  location: {
    pickup: string;
    dropoff: string;
  };
  payment: {
    amount: number;
    dailyRate: number;
    securityDeposit: number;
    securityDepositStatus: 'pending' | 'held' | 'released' | 'partial_charged' | 'charged' | 'refunded' | null;
    securityDepositHeldAt: string | null;
    securityDepositReleasedAt: string | null;
    method: string;
    reference: string | null;
    status: string;
  };
  serviceType: string;
  specialRequests: string | null;
  notes: any;
  cancellationReason: string | null;
  createdAt: string;
  updatedAt: string;
  canExtend: boolean;
  canModify: boolean;
  canCancel: boolean;
  canRefund: boolean;

  // Cost breakdown fields (V2 now includes these)
  rentalCost: number;
  bookingFee: string;
  vatAmount: number;
  vatRate: number;
  bookingFeeRate: number;
  serviceFee: number;
  serviceFeeRate: number;
  driverFee: number;
  deliveryFee: number;
  costBreakdown: {
    basePrice: number;
    rentalCost: number;
    bookingFee: number;
    bookingFeeRate: number;
    serviceFee: number;
    serviceFeeRate: number;
    vat: number;
    vatRate: number;
    securityDeposit: number;
    totalAmount: number;
    hostPayout: number;
  };
}

// ========================================
// Service Classification Enums
export enum ServiceCategory {
  VEHICLE_RENTAL = 'vehicle-rental',
  DRIVER_RENTAL = 'driver-rental',
  YACHT_RENTAL = 'yacht-rental',
  PACKAGE_SERVICE = 'package-service'
}

// Vehicle Services
export enum VehicleServiceType {
  SELF_DRIVE = 'self-drive',
  CHAUFFEUR_DRIVEN = 'chauffeur-driven',
  AIRPORT_TRANSFER = 'airport-transfer'
}

// Yatch Services
export enum YachtServiceType {
  CRUISE = 'cruise',
  TRIP = 'trip'
}

// Driver Services
export enum DriverServiceType {
  HOURLY = 'hourly',
  DAILY = 'daily'
}

export enum TripType {
  PICKUP = 'pickup',
  DROPOFF = 'dropoff',
  ROUND_TRIP = 'round-trip'
}

export enum BookingApprovalType {
  INSTANT = 'instant',
  REQUEST_TO_BOOK = 'request-to-book',
  ADMIN_APPROVAL = 'admin-approval',
  DUAL_APPROVAL = 'dual-approval'
}

export enum BookingDurationType {
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

// export enum BookingComplexity {
//   SIMPLE = 'simple',
//   COMPLEX = 'complex',
//   ENTERPRISE = 'enterprise'
// }

export enum BookingStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  ACTIVE = 'active',
  // IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  NO_SHOW = 'no_show'
}

export enum BookingWorkflowStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  OWNER_APPROVED = 'owner_approved',
  ADMIN_APPROVED = 'admin_approved',
  REJECTED = 'rejected',
  CONFIRMED = 'confirmed',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED_BY_CUSTOMER = 'cancelled_by_customer',
  CANCELLED_BY_OWNER = 'cancelled_by_owner',
  CANCELLED_BY_ADMIN = 'cancelled_by_admin'
}

// Legacy booking type enum for backward compatibility
export enum LegacyBookingType {
  INSTANT = 'instant',
  SELF_DRIVE = 'self_drive',
  WITH_DRIVER = 'with_driver',
  REQUEST_TO_BOOK = 'request_to_book'
}

// Pricing Types
export interface PricingBreakdown {
  baseRate: number;
  chauffeurSurcharge?: number;
  driverFee?: number;
  serviceFee?: number;
  insuranceFee?: number;
  fuelSurcharge?: number;
  tollCharges?: number;
  subtotal: number;
  taxAmount?: number;
  discountAmount?: number;
  totalAmount: number;
  securityDeposit?: number;
  currency?: string;
  breakdown?: {
    baseService: number;
    chauffeurService?: number;
    additionalServices?: number;
    platformFee: number;
    fees: number;
    taxes: number;
  };
}

// Booking Classification
export interface BookingClassification {
  serviceCategory: ServiceCategory;
  primaryServiceType: VehicleServiceType | DriverServiceType | YachtServiceType;
  approvalType: BookingApprovalType;
  durationType: BookingDurationType;
  // complexity: BookingComplexity;
  isHighValue: boolean;
  isRecurring: boolean;
  isEnterprise: boolean;
  requiresSpecialHandling: boolean;
}

// Enhanced Booking Interface
export interface Booking {
  id: string;
  bookingNumber: string;
  bookingReference?: string;

  // Enhanced Classification
  serviceCategory: ServiceCategory;
  primaryServiceType: VehicleServiceType | DriverServiceType | YachtServiceType;
  approvalType: BookingApprovalType;
  durationType: BookingDurationType;
  // complexity: BookingComplexity;

  // Status and Workflow
  status: BookingStatus;
  workflowStatus?: BookingWorkflowStatus;
  requiresApproval: boolean;
  estimatedConfirmationTime?: string;

  // Classification Flags
  isHighValue: boolean;
  isRecurring: boolean;
  isEnterprise: boolean;
  requiresSpecialHandling: boolean;

  // Basic Information
  customerId: string;
  customer: User;
  customerTier?: string;

  // Entities
  vehicleId?: string;
  vehicle?: Vehicle;
  driverId?: string;
  driver?: Driver;
  primaryDriverId?: string;
  backupDriverId?: string;
  yachtId?: string;
  ownerId?: string;

  // Service Details
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  totalDays?: number;
  totalHours?: number;
  pickupLocation: string;
  dropoffLocation?: string;

  // Service Configuration
  requiresDriverAccommodation?: boolean;
  includesAirportService?: boolean;
  isLongDistance?: boolean;
  isCrossState?: boolean;
  additionalServices?: string[];
  specialRequests?: string[];

  // Enhanced Pricing
  pricing: PricingBreakdown;
  totalAmount: number;
  currency?: string;

  // Payment and Financial
  paymentStatus?: string;
  paymentMethod?: string;
  stripePaymentIntentId?: string;
  refundAmount?: number;
  refundReason?: string;

  // Operational
  notes?: string;
  customerNotes?: string;
  ownerNotes?: string;
  adminNotes?: string;
  driverNotes?: string;

  // Trip Tracking
  checkinTime?: string;
  checkoutTime?: string;
  checkinMileage?: number;
  checkoutMileage?: number;
  totalMilesDriven?: number;
  checkinPhotos?: string[];
  checkoutPhotos?: string[];

  // Cancellation
  cancellationReason?: string;
  cancelledBy?: string;
  cancelledAt?: string;
  cancellationFee?: number;
  isRefundable: boolean;

  // Quality and Feedback
  customerRating?: number;
  customerFeedback?: string;
  ownerRating?: number;
  ownerFeedback?: string;
  hasIncident: boolean;
  incidentReport?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
}

// Legacy Booking Interface for backward compatibility
export interface LegacyBooking {
  id: string;
  bookingNumber: string;
  type: LegacyBookingType;
  status: BookingStatus;
  customerId: string;
  customer: User;
  vehicleId: string;
  vehicle: Vehicle;
  driverId?: string;
  driver?: Driver;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation: string;
  totalAmount: number;
  currency: string;
  specialRequests?: string;
  createdAt: string;
  updatedAt: string;
}

// Form Types
export interface BookingForm {
  // Service Classification
  serviceCategory: ServiceCategory;
  serviceType: VehicleServiceType | DriverServiceType | YachtServiceType;
  durationType: BookingDurationType;
  tripType?: TripType;

  // Entity IDs
  customerId: string;
  vehicleId?: string;
  driverId?: string;
  yachtId?: string;

  // Booking Details
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  pickupLocation: string;
  dropoffLocation?: string;

  // Service Configuration
  requiresDriverAccommodation?: boolean;
  includesAirportService?: boolean;
  isLongDistance?: boolean;
  isCrossState?: boolean;

  // Additional Details
  additionalServices?: string[];
  specialRequests?: string[];
  notes?: string;
  estimatedMileage?: number;

  // Payment
  paymentMethod?: string;
  promocode?: string;
}

// Legacy Booking Form for backward compatibility
export interface LegacyBookingForm {
  customerId: string;
  vehicleId: string;
  driverId?: string;
  type: LegacyBookingType;
  startDate: string;
  endDate: string;
  pickupLocation: string;
  dropoffLocation: string;
  specialRequests?: string;
}

// Approval Workflow
export interface ApprovalWorkflow {
  currentStep: string;
  nextStep?: string;
  estimatedTime: string;
  history: {
    step: string;
    status: 'approved' | 'rejected' | 'pending';
    approvedBy?: string;
    approvedAt?: string;
    notes?: string;
  }[];
  canApprove: boolean;
  canReject: boolean;
  approvalInstructions?: string;
}

// Booking Response from API
export interface CreateBookingResponse {
  booking: Booking;
  paymentRequired: boolean;
  nextStep: 'payment' | 'approval' | 'confirmation';
  classification: BookingClassification;
  approvalWorkflow?: ApprovalWorkflow;
  estimatedConfirmationTime?: string;
}

// Vehicle Booking Capabilities
export interface VehicleBookingOptions {
  vehicleId: string;
  vehicleInfo: {
    make: string;
    model: string;
    year: number;
    type: string;
    seats: number;
    images: string[];
  };
  serviceTypes: {
    type: VehicleServiceType;
    available: boolean;
    name: string;
    description: string;
    icon: string;
    surcharge?: number;
    restrictions: string[];
  }[];
  approvalType: BookingApprovalType;
  instantBookingEnabled: boolean;
  requiresOwnerApproval: boolean;
  durationOptions: {
    type: BookingDurationType;
    available: boolean;
    minimumHours?: number;
    rate: number;
    name: string;
    description: string;
  }[];
  pricing: {
    baseRates: {
      hourly?: number;
      daily: number;
      weekly?: number;
    };
    surcharges: {
      chauffeur?: number;
      insurance?: number;
      delivery?: number;
    };
    deposits: {
      security: number;
      fuel?: number;
    };
  };
  requirements: {
    minimumAge: number;
    validLicense: boolean;
    creditCard: boolean;
    deposit: number;
  };
  policies: {
    cancellationDeadline: number;
    cancellationFee: number;
    lateFee: number;
    mileageLimit: number;
    extraMileageFee: number;
  };
  availability: {
    instantBooking: boolean;
    advanceBookingRequired: number;
    maxAdvanceBooking: number;
  };
}

// Filter Types
export interface FilterOptions {
  search?: string;
  status?: BookingStatus[];
  workflowStatus?: BookingWorkflowStatus[];

  // Enhanced Service Filters
  serviceCategory?: ServiceCategory[];
  serviceType?: (VehicleServiceType | DriverServiceType | YachtServiceType)[];
  durationType?: BookingDurationType[];
  // complexity?: BookingComplexity[];
  approvalType?: BookingApprovalType[];

  // Business Classification Filters
  isHighValue?: boolean;
  isRecurring?: boolean;
  isEnterprise?: boolean;
  requiresSpecialHandling?: boolean;
  requiresApproval?: boolean;

  // Date Range Filters
  dateRange?: {
    start: string;
    end: string;
  };
  createdRange?: {
    start: string;
    end: string;
  };

  // Amount Filters
  minAmount?: number;
  maxAmount?: number;

  // Entity Filters
  customerId?: string;
  customerTier?: string[];
  vehicleId?: string;
  driverId?: string;
  ownerId?: string;

  // Location and Service Area
  location?: string[];
  serviceAreas?: string[];
  pickupLocation?: string;
  dropoffLocation?: string;

  // Additional Filters
  hasSpecialRequests?: boolean;
  hasAdditionalServices?: boolean;
  paymentStatus?: string[];
  hasIncidents?: boolean;

  // Legacy support
  type?: string[];

  // Sorting and Pagination
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface BookingFilterState extends FilterOptions {
  isActive: boolean;
  activeFiltersCount: number;
  quickFilters: {
    pending: boolean;
    highValue: boolean;
    requiresApproval: boolean;
    instant: boolean;
  };
}

// UI Display Props
export interface BookingStatusDisplayProps {
  status: BookingStatus;
  workflowStatus?: BookingWorkflowStatus;
  approvalType: BookingApprovalType;
  requiresApproval: boolean;
  estimatedConfirmationTime?: string;
  size?: 'sm' | 'md' | 'lg';
  showEstimatedTime?: boolean;
}

export interface ServiceTypeDisplayProps {
  serviceCategory: ServiceCategory;
  serviceType: VehicleServiceType | DriverServiceType | YachtServiceType;
  durationType: BookingDurationType;
  // complexity?: BookingComplexity;
  size?: 'sm' | 'md' | 'lg';
  showDetails?: boolean;
}
